import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  IconButton,
  Typography,
  Box,
  Autocomplete,
  CircularProgress,
  FormControlLabel,
  Switch
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { postData, updateData, fetchData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';

const BankModal = ({
  open,
  onClose,
  onSave,
  bankData = null,
  mode = 'create', // 'create', 'edit', 'view'
  balanceSheetHeads = [],
  existingBanks = [] // Add this prop to receive the list of existing banks
}) => {
  const { companyId, userId, token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);
  const [countries, setCountries] = useState([]);
  const [countryLoading, setCountryLoading] = useState(false);
  const [currencies, setCurrencies] = useState([]);
  const [currencyLoading, setCurrencyLoading] = useState(false);

  const [formData, setFormData] = useState({
    BalanceSheetHeadId: '',
    Name: '',
    AccountName: '',
    AccountNumber: '',
    IFSCCode: '',
    SwiftCode: '',
    BranchName: '',
    BranchAddress: '',
    ContactNumber: '',
    PaymentQRcodeUPIID: '',
    UPIName: '',
    IsPrimary: false,
    IsPayableOrReceivable: false,
    CountryId: null,
    ISOCode: '',
    OpeningBalance: '',
    OpeningDate: '',
    CurrencyTypeId: null,
    CurrencyName: '',
    MasterId: null
  });

  const [errors, setErrors] = useState({});
  const [primaryWarningDialogOpen, setPrimaryWarningDialogOpen] = useState(false);
  const [primaryWarningMessage, setPrimaryWarningMessage] = useState('');

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 600);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchCountries = async () => {
    setCountryLoading(true);
    try {
      const response = await fetchData('CountryApi', `/${companyId}`, token);
      // Handle array or object response
      if (Array.isArray(response)) {
        setCountries(response);
      } else if (response?.response_data && Array.isArray(response.response_data)) {
        setCountries(response.response_data);
      } else {
        setCountries([]);
      }
    } catch (error) {
      console.error('Error fetching countries:', error);
      setCountries([]);
    } finally {
      setCountryLoading(false);
    }
  };

  const fetchCurrencies = async () => {
    setCurrencyLoading(true);
    try {
      const response = await fetchData('CurrencyTypeApi', `/${companyId}`, token);
      // Handle array or object response
      if (Array.isArray(response)) {
        setCurrencies(response);
      } else if (response?.response_data && Array.isArray(response.response_data)) {
        setCurrencies(response.response_data);
      } else {
        setCurrencies([]);
      }
    } catch (error) {
      console.error('Error fetching currencies:', error);
      setCurrencies([]);
    } finally {
      setCurrencyLoading(false);
    }
  };

  const fetchSingleItem = async (id) => {
    try {
      const response = await fetchData('BankApi', `/BankDetails/${id}`, token);
      console.log('API Response:', response);
      let data = null;
      if (response.response_data) {
        data = response.response_data;
      } else if (response.Master || response.Bank) {
        data = response;
      } else if (response.responseCode === '1111' && response.response_data) {
        data = response.response_data;
      }
      if (data) {
        return {
          master: data.Master || data.master || {},
          bank: data.Bank || data.bank || {}
        };
      }
      return null;
    } catch (error) {
      console.error('Error fetching Bank details:', error);
      return null;
    }
  };

  useEffect(() => {
    if (open) {
      fetchCountries();
      fetchCurrencies();
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      console.log('Modal opened with mode:', mode, 'bankData:', bankData);

      if (bankData && mode !== 'create') {
        console.log('Loading existing bank data for edit/view, MasterId:', bankData.MasterId);
        const loadBankDetails = async () => {
          const details = await fetchSingleItem(bankData.MasterId);
          if (details) {
            setFormData({
              Id: details.bank.Id || bankData.Id || '',
              MasterId: bankData.MasterId || '',
              BalanceSheetHeadId: details.master.BalanceSheetHeadId || bankData.BalanceSheetHeadId || '',
              Name: details.master.Name || bankData.Name || '',
              AccountName: details.bank.AccountName || bankData.AccountName || '',
              AccountNumber: details.bank.AccountNumber || bankData.AccountNumber || '',
              IFSCCode: details.bank.IFSCCode || bankData.IFSCCode || '',
              SwiftCode: details.bank.SwiftCode || bankData.SwiftCode || '',
              BranchName: details.bank.BranchName || bankData.BranchName || '',
              BranchAddress: details.bank.BranchAddress || bankData.BranchAddress || '',
              ContactNumber: details.bank.ContactNumber || bankData.ContactNumber || '',
              PaymentQRcodeUPIID: details.bank.PaymentQRcodeUPIID || bankData.PaymentQRcodeUPIID || '',
              UPIName: details.bank.UPIName || bankData.UPIName || '',
              IsPrimary: details.bank.IsPrimary || bankData.IsPrimary || false,
              IsPayableOrReceivable: details.bank.IsPayableOrReceivable || bankData.IsPayableOrReceivable || false,
              CountryId: details.bank.CountryId || bankData.CountryId || null,
              ISOCode: details.bank.ISOCode || bankData.ISOCode || '',
              OpeningBalance: details.master.OpeningBalance !== null && details.master.OpeningBalance !== undefined ? details.master.OpeningBalance : (bankData.OpeningBalance || ''),
              OpeningDate: details.master.OpeningDate || bankData.OpeningDate || '',
              CurrencyTypeId: details.bank.CurrencyTypeId || bankData.CurrencyTypeId || null,
              CurrencyName: details.bank.CurrencyName || bankData.CurrencyName || '',
              CreatedBy: details.master.CreatedBy || bankData.CreatedBy || null,
              CreatedDate: details.master.CreatedDate || bankData.CreatedDate || null
            });
          } else {
            // Fallback to bankData if API call fails
            setFormData({
              Id: bankData.Id || '',
              MasterId: bankData.MasterId || '',
              BalanceSheetHeadId: bankData.BalanceSheetHeadId || '',
              Name: bankData.Name || '',
              AccountName: bankData.AccountName || '',
              AccountNumber: bankData.AccountNumber || '',
              IFSCCode: bankData.IFSCCode || '',
              SwiftCode: bankData.SwiftCode || '',
              BranchName: bankData.BranchName || '',
              BranchAddress: bankData.BranchAddress || '',
              ContactNumber: bankData.ContactNumber || '',
              PaymentQRcodeUPIID: bankData.PaymentQRcodeUPIID || '',
              UPIName: bankData.UPIName || '',
              IsPrimary: bankData.IsPrimary || false,
              IsPayableOrReceivable: bankData.IsPayableOrReceivable || false,
              CountryId: bankData.CountryId || null,
              ISOCode: bankData.ISOCode || '',
              OpeningBalance: bankData.OpeningBalance || '',
              OpeningDate: bankData.OpeningDate || '',
              CurrencyTypeId: bankData.CurrencyTypeId || null,
              CurrencyName: bankData.CurrencyName || ''
            });
          }
        };
        loadBankDetails();
      } else {
        console.log('Creating new bank');
        // Reset form for create mode
        setFormData({
          BalanceSheetHeadId: '',
          Name: '',
          AccountName: '',
          AccountNumber: '',
          IFSCCode: '',
          SwiftCode: '',
          BranchName: '',
          BranchAddress: '',
          ContactNumber: '',
          PaymentQRcodeUPIID: '',
          UPIName: '',
          IsPrimary: false,
          IsPayableOrReceivable: false,
          CountryId: null,
          ISOCode: '',
          OpeningBalance: '',
          OpeningDate: '',
          CurrencyTypeId: null,
          CurrencyName: '',
          MasterId: null
        });
      }
      setErrors({});
    }
  }, [open, bankData, mode]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Validate Account Number - only allow digits
    if (name === 'AccountNumber' && value) {
      const accountNumberPattern = /^[0-9]*$/;
      if (!accountNumberPattern.test(value)) {
        return;
      }
    }

    // Validate IFSC Code - only allow uppercase letters and digits
    if (name === 'IFSCCode' && value) {
      const ifscPattern = /^[A-Z0-9]*$/;
      if (!ifscPattern.test(value)) {
        return;
      }
    }

    // Validate Swift Code - only allow uppercase letters and digits
    if (name === 'SwiftCode' && value) {
      const swiftPattern = /^[A-Z0-9]*$/;
      if (!swiftPattern.test(value)) {
        return;
      }
    }

    // Validate Contact Number - only allow digits, spaces, +, -, and ()
    if (name === 'ContactNumber' && value) {
      const numericPattern = /^[0-9+\-\s()]*$/;
      if (!numericPattern.test(value)) {
        return;
      }
    }

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.Name.trim()) {
      newErrors.Name = 'Bank name is required';
    }

    if (!formData.BalanceSheetHeadId) {
      newErrors.BalanceSheetHeadId = 'Balance Sheet Head is required';
    }

    if (!formData.AccountNumber.trim()) {
      newErrors.AccountNumber = 'Account number is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const checkPrimaryBank = () => {
    // Only check if user is trying to set this bank as primary
    if (!formData.IsPrimary) {
      return null;
    }

    // Check if another bank already has primary status
    const existingPrimaryBank = existingBanks.find(bank => {
      // Skip the current bank in edit mode
      if (mode === 'edit' && bankData && bank.Id === bankData.Id) {
        return false;
      }

      // Check if another bank is marked as primary
      return bank.IsPrimary === true;
    });

    return existingPrimaryBank;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    // Check for existing primary bank
    const existingPrimaryBank = checkPrimaryBank();
    if (existingPrimaryBank) {
      setPrimaryWarningMessage(`Another bank "${existingPrimaryBank.Name}" is already marked as primary. Only one bank can be set as primary at a time.`);
      setPrimaryWarningDialogOpen(true);
      return;
    }

    setLoading(true);
    try {
      const masterId = mode === 'edit' ? formData.MasterId : 0;
      const bankId = mode === 'edit' ? formData.Id : 0;

      // Structure data with Master and Bank nested objects
      const dataToSend = {
        Master: {
          Id: masterId,
          CompanyId: companyId,
          BalanceSheetHeadId: parseInt(formData.BalanceSheetHeadId) || 0,
          MasterTypeId: formData.MasterTypeId || 5,
          Name: formData.Name,
          SearchableName: formData.Name,
          OpeningBalance: parseFloat(formData.OpeningBalance) || 0,
          OpeningDate: formData.OpeningDate ? formData.OpeningDate.split('T')[0] : new Date().toISOString().split('T')[0],
          IsPayableOrReceivable: formData.IsPayableOrReceivable ?? false,
          IsActive: true,
          CreatedBy: mode === 'create' ? userId : (formData.CreatedBy || userId),
          ModifiedBy: mode === 'edit' ? userId : null,
          CreatedDate: mode === 'edit' ? formData.CreatedDate : null,
          ModifiedDate: mode === 'edit' ? new Date().toISOString() : null
        },
        Bank: {
          Id: bankId,
          MasterId: masterId || mode === 'edit' ? formData.MasterId : 0,
          CompanyId: companyId,
          AccountName: formData.AccountName,
          AccountNumber: formData.AccountNumber,
          IFSCCode: formData.IFSCCode,
          SwiftCode: formData.SwiftCode,
          BranchName: formData.BranchName,
          BranchAddress: formData.BranchAddress,
          ContactNumber: formData.ContactNumber,
          PaymentQRcodeUPIID: formData.PaymentQRcodeUPIID,
          UPIName: formData.UPIName,
          IsPrimary: formData.IsPrimary,
          IsPayableOrReceivable: formData.IsPayableOrReceivable,
          CountryId: formData.CountryId,
          CurrencyTypeId: formData.CurrencyTypeId,
          CurrencyName: formData.CurrencyName,
          ISOCode: formData.ISOCode,
          IsActive: true,
          CreatedBy: mode === 'create' ? userId : (formData.CreatedBy || userId),
          ModifiedBy: mode === 'edit' ? userId : null,
          CreatedDate: mode === 'edit' ? formData.CreatedDate : null,
          ModifiedDate: mode === 'edit' ? new Date().toISOString() : null
        }
      };

      if (mode === 'edit') {
        // Validate that we have the bank ID
        if (!formData.Id) {
          console.error('Cannot update: Missing bank ID');
          alert('Error: Cannot update bank - missing ID');
          return;
        }

        console.log('Updating bank with ID:', formData.Id);
        const response = await updateData('BankApi', formData.Id, dataToSend, token);
        console.log('Update response:', response);
      } else {
        // For create mode
        console.log('Creating new bank');
        const response = await postData('BankApi', '', dataToSend, token);
        console.log('Create response:', response);
      }

      onSave();
      onClose();
    } catch (error) {
      console.error('Error saving bank:', error);
      alert('Error saving bank: ' + (error.response?.data?.title || error.message));
    } finally {
      setLoading(false);
    }
  };

  const getTitle = () => {
    switch (mode) {
      case 'create': return 'New Bank';
      case 'edit': return 'Edit Bank';
      case 'view': return 'Bank Details';
      default: return 'Bank';
    }
  };

  const isReadOnly = mode === 'view';

  // Common input styles
  const inputStyle = { borderRadius: '6px', fontSize: '13px', height: '32px', textAlign: 'right' };
  const labelStyle = { fontSize: '11px' };
  const helperStyle = { fontSize: '9px', margin: '1px 0 0 0' };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          style: {
            borderRadius: isMobile ? '0' : '12px',
            background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
            padding: '2px',
            margin: isMobile ? '0' : '8px'
          }
        }}
      >
        <Box style={{
          background: 'white',
          borderRadius: isMobile ? '0' : '10px',
          overflow: 'hidden',
          height: isMobile ? '100vh' : 'auto'
        }}>
          <DialogTitle style={{
            background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
            color: 'white',
            padding: '8px 12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            minHeight: 'auto'
          }}>
            <Box style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <AccountBalanceIcon style={{ fontSize: '18px' }} />
              <Typography variant="h6" style={{ fontWeight: '600', fontSize: '14px' }}>
                {getTitle()}
              </Typography>
            </Box>
            <IconButton
              onClick={onClose}
              style={{ color: 'white', padding: '2px' }}
              size="small"
            >
              <CloseIcon style={{ fontSize: '18px' }} />
            </IconButton>
          </DialogTitle>

          <DialogContent style={{
            padding: '8px 12px',
            paddingBottom: '4px'
          }}>
            <Typography variant="subtitle2" style={{
              color: '#666',
              marginBottom: '6px',
              fontSize: '10px',
              lineHeight: '1.2'
            }}>
              {mode === 'create' ? 'Add a new bank to your system' :
                mode === 'edit' ? 'Update bank information' :
                  'View bank details'}
            </Typography>

            <Grid container spacing={1}>
              {/* Basic Information */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" style={{
                  fontWeight: '600',
                  marginBottom: '3px',
                  color: '#2d3748',
                  fontSize: '12px'
                }}>
                  Basic Information
                </Typography>
              </Grid>

              {/* Bank Name and Balance Sheet Head - Same Row */}
              <Grid item xs={12} sm={5}>
                <TextField
                  name="Name"
                  label="Bank Name *"
                  value={formData.Name}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  error={!!errors.Name}
                  helperText={errors.Name}
                  disabled={isReadOnly}
                  placeholder="Enter bank name"
                  InputProps={{ style: inputStyle }}
                  InputLabelProps={{ style: labelStyle }}
                  FormHelperTextProps={{ style: helperStyle }}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <TextField
                  select
                  name="BalanceSheetHeadId"
                  label="Balance Sheet Head *"
                  value={formData.BalanceSheetHeadId}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  error={!!errors.BalanceSheetHeadId}
                  helperText={errors.BalanceSheetHeadId}
                  disabled={isReadOnly}
                  SelectProps={{ native: true }}
                  InputProps={{ style: inputStyle }}
                  InputLabelProps={{ style: labelStyle }}
                  FormHelperTextProps={{ style: helperStyle }}
                >
                  <option value=""></option>
                  {balanceSheetHeads.map(head => (
                    <option key={head.Id} value={head.Id}>{head.Name}</option>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={2}>
                <TextField
                  name="OpeningBalance"
                  label="Opening Balance (AED)"
                  type="number"
                  value={formData.OpeningBalance}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  error={!!errors.OpeningBalance}
                  helperText={errors.OpeningBalance}
                  disabled={isReadOnly}
                  placeholder="0"
                  inputProps={{ step: "0.01", style: { textAlign: 'right' } }}
                  InputProps={{ style: inputStyle }}
                  InputLabelProps={{ style: labelStyle }}
                  FormHelperTextProps={{ style: helperStyle }}
                />
              </Grid>

              <Grid item xs={12} sm={2}>
                <TextField
                  name="OpeningDate"
                  label="Opening Date"
                  type="date"
                  value={formData.OpeningDate ? formData.OpeningDate.split('T')[0] : ''}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  error={!!errors.OpeningDate}
                  helperText={errors.OpeningDate}
                  disabled={isReadOnly}
                  InputLabelProps={{ shrink: true, style: labelStyle }}
                  InputProps={{ style: inputStyle }}
                  FormHelperTextProps={{ style: helperStyle }}
                />
              </Grid>

              {/* Account Information */}
              {/* <Grid item xs={12}>
                <Typography variant="subtitle1" style={{
                  fontWeight: '600',
                  marginBottom: '3px',
                  marginTop: '2px',
                  color: '#2d3748',
                  fontSize: '12px'
                }}>
                  Account Details
                </Typography>
              </Grid> */}

              <Grid item xs={12} sm={3}>
                <TextField
                  name="AccountNumber"
                  label="Account Number *"
                  value={formData.AccountNumber}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  error={!!errors.AccountNumber}
                  helperText={errors.AccountNumber}
                  disabled={isReadOnly}
                  placeholder="Enter account number"
                  inputProps={{ maxLength: 20 }}
                  InputProps={{ style: inputStyle }}
                  InputLabelProps={{ style: labelStyle }}
                  FormHelperTextProps={{ style: helperStyle }}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <TextField
                  name="AccountName"
                  label="Account Name"
                  value={formData.AccountName}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  disabled={isReadOnly}
                  placeholder="Enter account name"
                  inputProps={{ maxLength: 100 }}
                  InputProps={{ style: inputStyle }}
                  InputLabelProps={{ style: labelStyle }}
                  FormHelperTextProps={{ style: helperStyle }}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <TextField
                  name="IFSCCode"
                  label="IBAN *"
                  value={formData.IFSCCode}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  error={!!errors.IFSCCode}
                  helperText={errors.IFSCCode}
                  disabled={isReadOnly}
                  placeholder="Enter IBAN"
                  inputProps={{ maxLength: 11 }}
                  InputProps={{ style: inputStyle }}
                  InputLabelProps={{ style: labelStyle }}
                  FormHelperTextProps={{ style: helperStyle }}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <TextField
                  name="SwiftCode"
                  label="Swift Code"
                  value={formData.SwiftCode}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  disabled={isReadOnly}
                  placeholder="Enter SWIFT/BIC code"
                  inputProps={{ maxLength: 11 }}
                  InputProps={{ style: inputStyle }}
                  InputLabelProps={{ style: labelStyle }}
                  FormHelperTextProps={{ style: helperStyle }}
                />
              </Grid>

              {/* Country Selection */}
              <Grid item xs={12} sm={3}>
                <Autocomplete
                  options={Array.isArray(countries) ? countries : []}
                  getOptionLabel={(option) => option.Name ? `${option.Name} (${option.ISOCode})` : ''}
                  isOptionEqualToValue={(option, value) => option.Id === value.Id}
                  value={Array.isArray(countries) && formData.CountryId ? countries.find(c => c.Id === formData.CountryId) || null : null}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      setFormData(prev => ({
                        ...prev,
                        CountryId: newValue.Id,
                        ISOCode: newValue.ISOCode || ''
                      }));
                    } else {
                      setFormData(prev => ({
                        ...prev,
                        CountryId: null,
                        ISOCode: ''
                      }));
                    }
                  }}
                  disabled={isReadOnly || countryLoading}
                  loading={countryLoading}
                  ListboxProps={{
                    style: {
                      fontSize: '11px',
                      maxHeight: '200px'
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Country"
                      size="small"
                      placeholder="Select country"
                      InputProps={{
                        ...params.InputProps,
                        style: inputStyle,
                        endAdornment: (
                          <>
                            {countryLoading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                      InputLabelProps={{ style: labelStyle }}
                    />
                  )}
                />
              </Grid>

              {/* Currency Selection */}
              <Grid item xs={12} sm={3}>
                <Autocomplete
                  options={Array.isArray(currencies) ? currencies : []}
                  getOptionLabel={(option) => option.Currency || ''}
                  isOptionEqualToValue={(option, value) => option.Id === value.Id}
                  value={Array.isArray(currencies) && formData.CurrencyTypeId ? currencies.find(c => c.Id === formData.CurrencyTypeId) || null : null}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      setFormData(prev => ({
                        ...prev,
                        CurrencyTypeId: newValue.Id,
                        CurrencyName: newValue.Currency || ''
                      }));
                    } else {
                      setFormData(prev => ({
                        ...prev,
                        CurrencyTypeId: null,
                        CurrencyName: ''
                      }));
                    }
                  }}
                  disabled={isReadOnly || currencyLoading}
                  loading={currencyLoading}
                  ListboxProps={{
                    style: {
                      fontSize: '11px',
                      maxHeight: '200px'
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Currency"
                      size="small"
                      placeholder="Select currency"
                      InputProps={{
                        ...params.InputProps,
                        style: { ...inputStyle, fontSize: '11.5px' },
                        endAdornment: (
                          <>
                            {currencyLoading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                      InputLabelProps={{ style: labelStyle }}
                    />
                  )}
                />
              </Grid>

              {/* Branch Information */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" style={{
                  fontWeight: '600',
                  marginBottom: '3px',
                  marginTop: '2px',
                  color: '#2d3748',
                  fontSize: '12px'
                }}>
                  Branch Details
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  name="BranchName"
                  label="Branch Name"
                  value={formData.BranchName}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  disabled={isReadOnly}
                  placeholder="Enter branch name"
                  inputProps={{ maxLength: 100 }}
                  InputProps={{ style: inputStyle }}
                  InputLabelProps={{ style: labelStyle }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  name="ContactNumber"
                  label="Contact Number"
                  value={formData.ContactNumber}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  disabled={isReadOnly}
                  placeholder="+91 XXXXX XXXXX"
                  inputProps={{ maxLength: 15 }}
                  InputProps={{ style: inputStyle }}
                  InputLabelProps={{ style: labelStyle }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  name="BranchAddress"
                  label="Branch Address"
                  value={formData.BranchAddress}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  disabled={isReadOnly}
                  placeholder="Enter branch address"
                  multiline
                  rows={2}
                  inputProps={{ maxLength: 200 }}
                  InputProps={{ style: { borderRadius: '6px', fontSize: '13px' } }}
                  InputLabelProps={{ style: labelStyle }}
                />
              </Grid>

              {/* UPI Information */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" style={{
                  fontWeight: '600',
                  marginBottom: '3px',
                  marginTop: '2px',
                  color: '#2d3748',
                  fontSize: '12px'
                }}>
                  UPI Details
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  name="PaymentQRcodeUPIID"
                  label="UPI ID"
                  value={formData.PaymentQRcodeUPIID}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  disabled={isReadOnly}
                  placeholder="example@upi"
                  inputProps={{ maxLength: 50 }}
                  InputProps={{ style: inputStyle }}
                  InputLabelProps={{ style: labelStyle }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  name="UPIName"
                  label="UPI Name"
                  value={formData.UPIName}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  disabled={isReadOnly}
                  placeholder="Enter UPI name"
                  inputProps={{ maxLength: 50 }}
                  InputProps={{ style: inputStyle }}
                  InputLabelProps={{ style: labelStyle }}
                />
              </Grid>

              {/* Primary Bank Setting */}
              <Grid item xs={12}>
                <Box style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '4px 8px',
                  border: '1px solid #e0e0e0',
                  borderRadius: '4px',
                  backgroundColor: isReadOnly ? '#f5f5f5' : 'white',
                  minHeight: '28px',
                  marginTop: '2px'
                }}>
                  <input
                    type="checkbox"
                    name="IsPrimary"
                    checked={formData.IsPrimary}
                    onChange={handleChange}
                    disabled={isReadOnly}
                    style={{
                      width: '12px',
                      height: '12px',
                      accentColor: '#0f3460'
                    }}
                  />
                  <Typography style={{
                    fontSize: '11px',
                    fontWeight: '500',
                    color: '#4a5568'
                  }}>
                    Set as Primary Bank
                  </Typography>
                </Box>
              </Grid>

              {/* Payable or Receivable Setting */}
              <Grid item xs={12}>
                <Box style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '4px 8px',
                  border: '1px solid #e0e0e0',
                  borderRadius: '4px',
                  backgroundColor: isReadOnly ? '#f5f5f5' : 'white',
                  minHeight: '28px',
                  marginTop: '2px'
                }}>
                  <input
                    type="checkbox"
                    name="IsPayableOrReceivable"
                    checked={formData.IsPayableOrReceivable}
                    onChange={handleChange}
                    disabled={isReadOnly}
                    style={{
                      width: '12px',
                      height: '12px',
                      accentColor: '#0f3460'
                    }}
                  />
                  <Typography style={{
                    fontSize: '11px',
                    fontWeight: '500',
                    color: '#4a5568'
                  }}>
                    Payable or Receivable
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions style={{
            padding: '8px 12px',
            background: '#f8f9fa',
            borderTop: '1px solid #e9ecef',
            gap: '6px',
            minHeight: 'auto'
          }}>
            <Button
              onClick={onClose}
              style={{
                color: '#6c757d',
                textTransform: 'none',
                fontWeight: '500',
                fontSize: '12px',
                padding: '4px 8px'
              }}
            >
              Cancel
            </Button>
            {!isReadOnly && (
              <Button
                onClick={handleSave}
                disabled={loading}
                variant="contained"
                style={{
                  background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
                  color: 'white',
                  textTransform: 'none',
                  fontWeight: '600',
                  borderRadius: '4px',
                  padding: '4px 12px',
                  fontSize: '12px'
                }}
              >
                {loading ? 'Saving...' : mode === 'create' ? 'Add Bank' : 'Update Bank'}
              </Button>
            )}
          </DialogActions>
        </Box>
      </Dialog>

      {/* Primary Bank Warning Dialog */}
      <Dialog
        open={primaryWarningDialogOpen}
        onClose={() => setPrimaryWarningDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: '12px',
            padding: '8px'
          }
        }}
      >
        <DialogContent style={{ textAlign: 'center', padding: '24px' }}>
          <WarningAmberIcon
            style={{
              fontSize: '64px',
              color: '#ff9800',
              marginBottom: '16px'
            }}
          />
          <Typography
            variant="h6"
            style={{
              fontWeight: '600',
              marginBottom: '12px',
              color: '#2d3748',
              fontSize: '16px'
            }}
          >
            Primary Bank Already Exists
          </Typography>
          <Typography
            style={{
              color: '#666',
              fontSize: '14px',
              lineHeight: '1.5'
            }}
          >
            {primaryWarningMessage}
          </Typography>
        </DialogContent>
        <DialogActions style={{ padding: '0 24px 24px 24px', justifyContent: 'center' }}>
          <Button
            onClick={() => setPrimaryWarningDialogOpen(false)}
            variant="contained"
            style={{
              background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
              color: 'white',
              textTransform: 'none',
              fontWeight: '600',
              borderRadius: '8px',
              padding: '8px 32px',
              fontSize: '14px'
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BankModal;
